'use client';

import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';

import { Check } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';

import { DemoLink } from '@/components/demo-link';
import { ScanEmailModal } from '@/components/modals/lazy';
import { FormFieldError } from '@/components/modals/shared';
import { REVEAL_EASE } from '@/components/reveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SCANNER_COPY } from '@/lib/content';
import { clearScanEmail, peekScanEmail } from '@/lib/scan-handoff';
import {
  scoreDomain,
  scoreTone,
  scoresOf,
  submitScanReport,
  type ScanDimension,
} from '@/lib/scan-score';
import { cn, CTA_LINK_STYLE } from '@/lib/utils';
import { EMAIL_REGEX, URL_REGEX } from '@/lib/validation';

type Phase = 'idle' | 'scanning' | 'result';

const SCANNER_ERROR_ID = 'scanner-error';

const STEP_MS = 850;
/** The card's gutter. Every row of it steps in together or the stack reads as ragged. */
const ROW_X = 'px-6 sm:px-9';
const DIMS = 4;
/** One full turn per scan, so the sweep and the four steps are the same clock. */
const SWEEP_MS = STEP_MS * DIMS;
/** Where the wedge's leading edge points at t=0. Off-axis, so the dots miss the crosshairs. */
const SWEEP_FROM_DEG = -20;
const RADAR_PX = 120;
const DOT_PX = 9;
/** Varied so four points on one clock face don't read as a dial. */
const DOT_RADIUS = [40, 44, 36, 42];

/**
 * A dot sits where the sweep will be at the moment its dimension resolves. Derived, not
 * hand-placed: move `STEP_MS` or the start angle and these follow, so the wedge and the dot it
 * lights can't drift out of step.
 */
const DOT_POS = Array.from({ length: DIMS }, (_, index) => {
  const degrees = SWEEP_FROM_DEG + (360 / DIMS) * (index + 1);
  const radians = (degrees * Math.PI) / 180;
  const centre = RADAR_PX / 2 - DOT_PX / 2;
  return {
    left: `${(centre + DOT_RADIUS[index] * Math.sin(radians)).toFixed(1)}px`,
    top: `${(centre - DOT_RADIUS[index] * Math.cos(radians)).toFixed(1)}px`,
  };
});

const TONE_TEXT: Record<ReturnType<typeof scoreTone>, string> = {
  brand: 'text-brand',
  warning: 'text-warning',
  success: 'text-success',
};

/** The pill behind a settled score. Kept apart from `TONE_TEXT`: the wash arrives under a legend
 *  that is still on its way out, so the two fade on their own clocks. */
const TONE_WASH: Record<ReturnType<typeof scoreTone>, string> = {
  brand: 'bg-brand/16',
  warning: 'bg-warning/16',
  success: 'bg-success/16',
};

function Radar({
  dims,
  resolvedCount,
  sweeping = true,
}: {
  dims: ScanDimension[];
  resolvedCount: number;
  /** Off once the scan has landed: a dish still turning over a finished result reads as stuck. */
  sweeping?: boolean;
}) {
  return (
    <div
      className="relative shrink-0"
      style={{ width: RADAR_PX, height: RADAR_PX }}
      aria-hidden="true"
    >
      <div className="absolute inset-0 rounded-full border" />
      <div className="absolute inset-5 rounded-full border opacity-70" />
      <div className="absolute inset-10 rounded-full border opacity-50" />
      {/* The graticule stays whatever the dish is doing — it is the instrument, not the activity. */}
      <span className="absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-current/10" />
      <span className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-current/10" />
      <span className="absolute top-1/2 left-1/2 size-1 -translate-1/2 rounded-full bg-current/25" />
      {/* Faded, not unmounted — pulling the wedge on the frame the scan lands reads as a
          re-render of the whole dish. */}
      <span
        className={cn(
          'absolute inset-0 transition-opacity duration-700 motion-reduce:hidden',
          sweeping ? 'opacity-100' : 'opacity-0',
        )}
      >
        <span
          className="absolute inset-0 animate-radar-ping rounded-full border border-success/60"
          style={{ animationDuration: `${SWEEP_MS}ms` }}
        />
        <span
          className="absolute inset-0 animate-radar-ping rounded-full border border-success/40"
          style={{ animationDuration: `${SWEEP_MS}ms`, animationDelay: `${SWEEP_MS / 2}ms` }}
        />
        <span
          className="absolute inset-0 animate-sweep rounded-full"
          style={{
            animationDuration: `${SWEEP_MS}ms`,
            background: `conic-gradient(from ${SWEEP_FROM_DEG}deg, oklch(0.65 0.16 145 / 0.38), transparent 70deg)`,
          }}
        />
      </span>
      {dims.map((dim, index) => {
        const resolved = index < resolvedCount;
        const tone = scoreTone(dim.score);
        const style = DOT_POS[index];
        // Unresolved it breathes on the sweep's clock, so four dots can't twinkle out of time with
        // the wedge; resolved it blooms once as the wedge crosses. Both animate the same
        // properties, and never at once — the pulse class comes off as the lock class goes on.
        return (
          <span
            key={dim.name}
            className={cn(
              'absolute rounded-full transition-colors duration-500',
              resolved
                ? cn('animate-dot-lock bg-current motion-reduce:animate-none', TONE_TEXT[tone])
                : 'bg-foreground/25',
              !resolved && sweeping && 'animate-live-pulse motion-reduce:animate-none',
            )}
            style={{
              ...style,
              width: DOT_PX,
              height: DOT_PX,
              animationDuration: resolved ? undefined : `${SWEEP_MS / 2}ms`,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * The ellipsis after "probing". All three glyphs are always in the markup inside a fixed 3ch slot
 * and only the clip moves, so the word ahead of it never shifts as the count goes round.
 */
function ProbeDots() {
  return (
    <span aria-hidden="true" className="inline-block w-[3ch] text-start">
      <span className="inline-block animate-probe-ellipsis overflow-hidden align-bottom motion-reduce:w-[3ch] motion-reduce:animate-none">
        ...
      </span>
    </span>
  );
}

/** The status on the right of a dimension row, crossfading through a blur so the score reads as
 *  arriving rather than as having always been there. */
function ProbeStatus({
  children,
  statusKey,
  className,
  wash,
}: {
  children: ReactNode;
  /** What a change of state is: the same text under the same key is not one, and must not refade. */
  statusKey: string;
  className?: string;
  /** The pill's fill once the score has settled. Absent while the dimension is still probing. */
  wash?: string;
}) {
  return (
    // Present for the whole run and transparent until the score lands, so it washes in underneath
    // rather than popping in with the text it holds. `min-h-5` pins its height across the legend →
    // chip size change, or the row twitches exactly as the two strings cross.
    <span
      className={cn(
        'relative inline-flex min-h-5 items-center justify-end rounded-full px-2.5 transition-colors duration-500',
        wash,
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {/* The outgoing string clears before the incoming one arrives, or two sentences are legible
            on top of each other. Opacity and scale only — with the pill washing in behind it,
            vertical travel too reads as shaken rather than settled. */}
        <motion.span
          key={statusKey}
          className={className}
          initial={{ opacity: 0, scale: 0.94, filter: 'blur(3px)' }}
          animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            transition: { duration: 0.42, delay: 0.24, ease: REVEAL_EASE },
          }}
          exit={{
            opacity: 0,
            scale: 0.94,
            filter: 'blur(3px)',
            transition: { duration: 0.24, ease: REVEAL_EASE },
          }}
        >
          {children}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/** The four states the right-hand cell of a dimension row passes through, as `ProbeStatus` props. */
function dimensionStatus({
  dim,
  phase,
  resolved,
  probing,
}: {
  dim: ScanDimension;
  phase: Phase;
  resolved: boolean;
  probing: boolean;
}): ComponentProps<typeof ProbeStatus> {
  const tone = scoreTone(dim.score);
  if (phase === 'result')
    return {
      statusKey: 'tag',
      wash: TONE_WASH[tone],
      className: cn('text-xs tracking-label', TONE_TEXT[tone]),
      children: dim.tag,
    };
  if (resolved) return { statusKey: 'legend', className: TONE_TEXT[tone], children: dim.legend };
  const faint = 'text-faint-foreground';
  if (probing)
    return {
      statusKey: 'probing',
      className: faint,
      children: (
        <>
          probing
          <ProbeDots />
        </>
      ),
    };
  return { statusKey: 'queued', className: faint, children: 'queued' };
}

/**
 * A row of the result that opens out rather than appearing at full height.
 *
 * `height: auto` is the point — the card's own height follows the animation frame by frame, so the
 * result assembles downward instead of the whole block arriving at once. `overflow-hidden` is what
 * keeps the content clipped while the row is shorter than it.
 */
function ExpandRow({
  delay = 0,
  className,
  children,
}: {
  delay?: number;
  className?: string;
  children: ReactNode;
}) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={reducedMotion ? false : { height: 0, opacity: 0 }}
      animate={{
        height: 'auto',
        opacity: 1,
        transition: {
          height: { duration: 0.4, ease: REVEAL_EASE, delay },
          opacity: { duration: 0.3, delay: delay + 0.1 },
        },
      }}
      // Closes without the stagger — on the loop these all leave at once, and delaying them apart
      // makes the card unpack itself backwards.
      exit={{ height: 0, opacity: 0, transition: { duration: 0.25, ease: REVEAL_EASE } }}
      className="overflow-hidden"
    >
      <div className={className}>{children}</div>
    </motion.div>
  );
}

/**
 * Picks up a handoff: the domain from ?domain= and the email from session storage. Reading
 * searchParams on the server would make /scan render on demand for every visitor, so both are read
 * on the client; needs a Suspense boundary around it.
 */
export function ReadinessScannerFromQuery({ className }: { className?: string }) {
  const domain = useSearchParams().get('domain');
  const [email] = useState(peekScanEmail);
  useEffect(() => clearScanEmail(), []);
  return (
    <ReadinessScanner
      className={className}
      initialDomain={domain ?? undefined}
      initialEmail={email}
    />
  );
}

/** A scan is only ever started with the email that paid for it, so the two travel together. */
type Scan = { scored: ReturnType<typeof scoreDomain>; email: string };

/** Auto-runs only when both halves of a handoff survived the trip; otherwise the visitor finishes
 *  the form themselves. */
function seedScan({ domain, email }: { domain?: string; email?: string }): Scan | null {
  if (!domain || !URL_REGEX.test(domain.trim())) return null;
  if (!email || !EMAIL_REGEX.test(email.trim())) return null;
  return { scored: scoreDomain(domain), email: email.trim() };
}

export function ReadinessScanner({
  className,
  initialDomain,
  initialEmail,
}: {
  className?: string;
  initialDomain?: string;
  initialEmail?: string;
}) {
  const [input, setInput] = useState(initialDomain ?? '');
  const [error, setError] = useState(false);
  const [askingEmail, setAskingEmail] = useState(false);
  const [everAsked, setEverAsked] = useState(false);
  // A domain and email handed over from another page mean the visitor already asked for this scan.
  // Seeding here runs it on first paint; making them fill the form again reads as a dead end.
  const [scan, setScan] = useState<Scan | null>(() =>
    seedScan({ domain: initialDomain, email: initialEmail }),
  );
  const [phase, setPhase] = useState<Phase>(scan ? 'scanning' : 'idle');
  const [progress, setProgress] = useState(0);
  const reportedScan = useRef<Scan | null>(null);
  // PostHog's React docs are explicit that the hook is the only safe accessor: importing the
  // singleton can read it before the provider initializes it.
  const posthog = usePostHog();

  const idle = phase === 'idle';

  function resetScan() {
    setScan(null);
    setPhase('idle');
    setProgress(0);
    // Cleared too, or a second scan of the same domain would be treated as the one already
    // reported and would never send its lead.
    reportedScan.current = null;
  }

  function askForEmail() {
    if (!URL_REGEX.test(input.trim())) {
      setError(true);
      return;
    }
    setError(false);
    setEverAsked(true);
    setAskingEmail(true);
  }

  function runScan(email: string) {
    setAskingEmail(false);
    setProgress(0);
    setPhase('scanning');
    setScan({ scored: scoreDomain(input), email });
  }

  // The staged reveal is driven from here rather than from runScan so that a scan and its teardown
  // share one lifecycle. Scheduled anywhere else, a remount (Strict Mode does this in dev) clears
  // timers that nothing will reschedule and the scan sits at "probing…" forever.
  useEffect(() => {
    if (!scan) return;
    const { scored } = scan;

    // The reveal is safe to replay on remount, the lead and its analytics are not, so those are
    // held to once per scan. scoreDomain is synchronous, so the report goes out with real scores
    // the moment the scan starts rather than waiting for the animation to finish.
    if (reportedScan.current !== scan) {
      reportedScan.current = scan;
      posthog?.capture('scan_started', { domain: scored.domain });
      void submitScanReport({
        scored,
        email: scan.email,
        source: 'scanner',
        distinctId: posthog?.get_distinct_id?.(),
      });
    }

    const ids = Array.from({ length: DIMS }, (_, index) => index + 1).map(step =>
      window.setTimeout(() => setProgress(step), STEP_MS * step),
    );
    ids.push(
      window.setTimeout(
        () => {
          setPhase('result');
          posthog?.capture('scan_completed', {
            domain: scored.domain,
            overall: scored.overall,
            tier: scored.tier,
            scores: scoresOf(scored),
          });
        },
        STEP_MS * (DIMS + 1),
      ),
    );
    return () => ids.forEach(id => window.clearTimeout(id));
  }, [scan, posthog]);

  const result = scan?.scored ?? null;
  const dims = result?.dims ?? [];

  return (
    <div
      // The anchor `MARKETING_ROUTES.scanner` points at.
      id="scanner"
      className={cn(
        'invert-surface mx-auto w-full max-w-[760px] overflow-hidden rounded-xl border shadow-lift',
        className,
      )}
    >
      {/* No heading: /scan's page title already asks this. `SCANNER_COPY.heading` still carries
          the phrasing for llms.txt. */}
      <div className={cn('flex flex-col gap-4.5 py-8', ROW_X)}>
        <div className="flex flex-col gap-2.5 sm:flex-row">
          {/* Once a scan is running the field stops being an input and becomes a label for what is
              being scanned, so it shows the domain the scan actually took rather than whatever is
              still sitting in the box. */}
          <Input
            value={idle ? input : (result?.domain ?? input)}
            disabled={!idle}
            onChange={e => {
              setInput(e.target.value);
              setError(false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && idle) askForEmail();
            }}
            placeholder={SCANNER_COPY.placeholder}
            aria-label="Your website address"
            aria-invalid={error}
            aria-describedby={error ? SCANNER_ERROR_ID : undefined}
            className="h-14 min-h-14 flex-1 px-4 font-mono text-base sm:h-12 sm:min-h-12"
          />
          <Button
            type="button"
            className="h-14 px-6 text-base font-semibold sm:h-12"
            // Nothing to do while the clock is running, and pressing it again mid-scan queued a
            // second run over the first. On the result it turns into the way back to the start.
            disabled={phase === 'scanning'}
            onClick={phase === 'result' ? resetScan : askForEmail}
          >
            {idle
              ? SCANNER_COPY.button
              : phase === 'scanning'
                ? SCANNER_COPY.buttonScanning
                : SCANNER_COPY.buttonAgain}
          </Button>
        </div>
        <FormFieldError
          id={SCANNER_ERROR_ID}
          error={error ? SCANNER_COPY.validationError : undefined}
        />
        <p className="text-base text-faint-foreground">
          {SCANNER_COPY.microcopyBefore}
          <DemoLink className={cn('text-muted-foreground', CTA_LINK_STYLE)}>
            {SCANNER_COPY.microcopyLink}
          </DemoLink>
          {SCANNER_COPY.microcopyAfter}
        </p>
      </div>

      {/* One radar row for the whole run, not one per phase. Branching it under `AnimatePresence`
          rebuilds the dish on the frame the scan lands, and a freshly mounted wedge has nothing to
          fade from. Only each row's right-hand cell changes; everything new to the result expands
          in above and below. */}
      {result && phase !== 'idle' && (
        <div className="border-t" aria-live="polite">
          <AnimatePresence initial={false}>
            {phase === 'result' && [
              <ExpandRow
                key="kicker"
                className={cn(
                  'flex items-center justify-between gap-4 border-b bg-muted py-4',
                  ROW_X,
                )}
              >
                <span className="font-mono text-sm tracking-label">
                  {SCANNER_COPY.resultKicker} · {result.domain}
                </span>
                <span className="rounded-full border px-2.5 py-0.5 font-mono text-xs tracking-label text-faint-foreground">
                  {SCANNER_COPY.simulatedLabel}
                </span>
              </ExpandRow>,
              <ExpandRow key="verdict" delay={0.1} className={cn('border-b py-5', ROW_X)}>
                <p className="text-lg leading-snug font-semibold">{result.verdict}</p>
              </ExpandRow>,
            ]}
          </AnimatePresence>

          <div
            className={cn(
              'flex flex-col items-center gap-7 py-6 transition-colors duration-500 sm:flex-row',
              ROW_X,
              phase === 'scanning' && 'bg-muted',
            )}
          >
            <Radar
              dims={dims}
              resolvedCount={phase === 'result' ? dims.length : progress}
              sweeping={phase === 'scanning'}
            />
            <div className="flex w-full flex-1 flex-col gap-2.5 font-mono text-sm">
              {dims.map((dim, index) => {
                const resolved = phase === 'result' || index < progress;
                const probing = phase === 'scanning' && index === progress;
                return (
                  <div
                    key={dim.name}
                    className={cn(
                      'flex items-center justify-between gap-4 transition-opacity duration-300',
                      resolved || probing ? 'opacity-100' : 'opacity-50',
                    )}
                  >
                    <span className="tracking-label">{dim.name.toUpperCase()}</span>
                    <ProbeStatus {...dimensionStatus({ dim, phase, resolved, probing })} />
                  </div>
                );
              })}
            </div>
          </div>

          <AnimatePresence initial={false}>
            {phase === 'result' && [
              <ExpandRow key="footer" delay={0.2} className={cn('border-t py-4', ROW_X)}>
                <p className="text-base text-muted-foreground">{SCANNER_COPY.resultFooter}</p>
              </ExpandRow>,
              <ExpandRow key="sent" delay={0.3} className={cn('border-t py-4', ROW_X)}>
                <p className="flex items-start gap-1.5 text-base">
                  <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" />
                  <span>
                    {SCANNER_COPY.emailSuccessPrefix}
                    <span className="font-mono text-base">{scan?.email}</span>.
                  </span>
                </p>
              </ExpandRow>,
              // The close gets a band of its own rather than sharing a line with the confirmation
              // above it — the report is a receipt, this is the next thing to do.
              <ExpandRow key="cta" delay={0.4} className={cn('border-t bg-muted py-6', ROW_X)}>
                <Button asChild className="w-full sm:w-auto">
                  <DemoLink>{SCANNER_COPY.emailDemoCta}</DemoLink>
                </Button>
              </ExpandRow>,
            ]}
          </AnimatePresence>
        </div>
      )}

      {everAsked && (
        <ScanEmailModal
          open={askingEmail}
          domain={input.trim()}
          onClose={() => setAskingEmail(false)}
          onConfirm={runScan}
        />
      )}
    </div>
  );
}
